import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from './App';
import axios from 'axios';
import { SERVER_URL } from './config';
import html2canvas from 'html2canvas';
import './Tables.css';


const TABLE_TYPES = {
  RECTANGLE18: "מלבן 18 סועדים",
  RECTANGLE24: "מלבן 24 סועדים",
  ROUND11: "עגול 11 סועדים",
  ROUND12: "עגול 12 סועדים",
  ROUND10: "עגול 10 סועדים",
  SQUARE10: "ריבוע 10 סועדים",
  SQUARE11: "ריבוע 11 סועדים",
  SQUARE12: "ריבוע 12 סועדים",
  SQUARE13: "ריבוע 13 סועדים",
  SQUARE14: "ריבוע 14 סועדים",
  ROUND20: "עגול 20 סועדים",
  SQUARE20: "אבירים 20 סועדים",
  RECTANGLE32: "מלבן 32 סועדים",
  RECTANGLE48: "מלבן 48 סועדים",
  DANCEFLOOR: "רחבת ריקודים",
  DJSTATION: "דוכן DJ",
  BARSTATION: "תחנת בר",
  ENTRANCE: "כניסה",
};

const TYPE_TO_CLASS = Object.fromEntries(
  Object.entries(TABLE_TYPES).map(([key, value]) => [value, key])
);

const Tables = () => {
  const [tables, setTables] = useState([]);
  const [nonTableItems, setNonTableItems] = useState([]);
  const [dinersNumber, setDinersNumber] = useState();
  const [nextId, setNextId] = useState(1);
  const [activeItemId, setActiveItemId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [removedIds, setRemovedIds] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showInviteList, setShowInviteList] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // State for the search input
  const [showMobileWarning, setShowMobileWarning] = useState(false);  
  const [newTableId, setNewTableId] = useState('');
  const [showChangeIdPopup, setShowChangeIdPopup] = useState(false);
  const [newTableIdInput, setNewTableIdInput] = useState('');
  const { user } = useContext(AppContext);

  useEffect(() => {
    const fetchAllData = async () => {
      await Promise.all([fetchTablesAndNonTables(), getInvitesWithoutTable()]);
    };
    fetchAllData();
  }, [user]);


  useEffect(() => {
    const isMobile = window.innerWidth < 768; // Adjust width threshold as needed
    if (isMobile) {
      setShowMobileWarning(true); // Show custom modal instead of confirm
    }
  }, []);
 

  const fetchTablesAndNonTables = async () => {
    try {
      const [tableResponse, nonTableResponse, dinersResponse] = await Promise.all([
        axios.get(`${SERVER_URL}tables/`, {
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        }),
        axios.get(`${SERVER_URL}nontables/`, {
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        }),
        axios.get(`${SERVER_URL}diners_number/`, {
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        }),
      ]);

      // Set tables, non-table items, and diners number
      setTables(tableResponse.data);
      setNonTableItems(nonTableResponse.data);
      setDinersNumber(dinersResponse.data.diners_number); // Extract the count value
  
      // Calculate the next table ID
      const highestId =
        tableResponse.data.reduce((max, table) => Math.max(max, table.table_id), 0) ||
        nonTableResponse.data.reduce((max, nonTable) => Math.max(max, nonTable.table_id), 0);
  
      setNextId(highestId + 1);
    } catch (error) {
      console.error("Error fetching tables and non-tables", error);
      alert("Failed to fetch tables or non-tables. Please try again.");
    }
  };
  
  
  const changeTableId = async (oldTableId, newTableId) => {
    if (!newTableId) return alert("אנא הזן מספר שולחן חדש");
  
    const newIdNumber = parseInt(newTableId, 10);
    if (isNaN(newIdNumber)) return alert("מספר שולחן לא תקין");
  
    const exists = tables.some((table) => table.table_id === newIdNumber);
    if (exists) {
      alert("שולחן עם מספר זה כבר קיים");
      return;
    }
  
    try {
      await axios.put(
        `${SERVER_URL}change_table_id/`,
        {
          old_table_id: oldTableId,
          new_table_id: newIdNumber,
        },
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        }
      );
  
      alert("מספר השולחן שונה בהצלחה");
      fetchTablesAndNonTables();
      closeModal();
    } catch (error) {
      console.error("Error changing table ID:", error);
      alert("שגיאה בשינוי מספר שולחן");
    }
  };
  

  const downloadData = async (endpoint, filename) => {
    try {
        const response = await axios.get(`${SERVER_URL}${endpoint}/`, {
            headers: { Authorization: `Token ${localStorage.getItem('token')}` },
            responseType: 'blob',
        });

        // Create a link element to download the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        alert(`${filename} has been downloaded successfully.`);
    } catch (error) {
        console.error(`Error downloading ${filename}`, error);
        alert(`Failed to download ${filename}. Please try again.`);
    }
};

const downloadImage = () => {
  const canvasElement = document.querySelector('.Canvas'); // The canvas or container to capture
  if (!canvasElement) {
      alert('No canvas element found!');
      return;
  }

  html2canvas(canvasElement)
      .then((canvas) => {
          const link = document.createElement('a');
          link.download = 'seating-arrangement.png'; // Name of the downloaded file
          link.href = canvas.toDataURL('image/png');
          link.click();
      })
      .catch((error) => {
          console.error('Error capturing the canvas:', error);
          alert('Failed to capture the canvas. Please try again.');
      });
};


const saveItem = async (endpoint, data, successCallback) => {
  try {
      await axios.post(`${SERVER_URL}${endpoint}/`, data, {
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      });
      if (successCallback) successCallback();
  } catch (error) {
      console.error(`Error saving item to ${endpoint}`, error);
      alert("Failed to save the item. Please try again.");
  }
};

const saveDiners = async (tableId, updatedDiners) => {
  console.log(tableId, updatedDiners)
  const payload = {
    table_id: tableId,
    diners: updatedDiners.map((diner) => ({
      id: diner.id, // Include diner ID
    })),
  };

  await saveItem("update-diners", payload, fetchTablesAndNonTables);
};


const saveTable = async (tableData) => {
  console.log("Saving table:", tableData);
  await saveItem('tables', tableData, fetchTablesAndNonTables);
};

const saveNonTable = async (nonTableData) => {
  await saveItem('nontables', nonTableData, fetchTablesAndNonTables);
};


const deleteItem = async (endpoint, id, successCallback) => {
  const isConfirmed = window.confirm("Are you sure you want to delete this item?");
  if (isConfirmed) {
      try {
          await axios.delete(`${SERVER_URL}${endpoint}/${id}/`, {
              headers: { Authorization: `Token ${localStorage.getItem('token')}` },
          });
          if (successCallback) successCallback();
      } catch (error) {
          console.error(`Error deleting item from ${endpoint}`, error);
          alert("Failed to delete the item. Please try again.");
      }
  }
};

const deleteTable = async (tableId, id) => {
  const isConfirmed = window.confirm("האם אתה בטוח שברצונך למחוק את השולחן?");
  if (isConfirmed) {
      try {
          await axios.delete(`${SERVER_URL}tables/${tableId}/`, {
              headers: { Authorization: `Token ${localStorage.getItem('token')}` },
          });

          // Filter out the deleted table
          const remainingTables = tables.filter((table) => table.id !== tableId);
          setTables(remainingTables);

          // Add the deleted table's ID to the removedIds list
          setRemovedIds((prev) => [...prev, id]);

          // Recalculate `nextId` based on remaining tables
          const highestId = remainingTables.reduce(
              (max, table) => Math.max(max, table.table_id),
              0
          );
          setNextId(highestId + 1);
      } catch (error) {
          console.error("Error deleting table", error);
      }
  }
};


const deleteNonTable = async (id) => {
  deleteItem('nontables', id, () => {
      const remainingNonTables = nonTableItems.filter((item) => item.id !== id);
      setNonTableItems(remainingNonTables);
  });
};


const deleteAllItems = async () => {
  const isConfirmed = window.confirm("Are you sure you want to delete all items?");
  if (isConfirmed) {
      try {
          await axios.delete(`${SERVER_URL}tables/delete_all/`, {
              headers: { Authorization: `Token ${localStorage.getItem('token')}` },
          });
          fetchTablesAndNonTables();
      } catch (error) {
          console.error("Error deleting all items", error);
          alert("Failed to delete all items. Please try again.");
      }
  }
};


const addTable = (type) => {
  console.log("nextId:", nextId, "removedIds:", removedIds);

  const newTableId = removedIds.length > 0 ? removedIds[0] : nextId || 1;

  if (!newTableId) {
      console.error("Error: table_id is not defined.");
      alert("Unable to add table due to invalid ID.");
      return;
  }

  const newTable = { table_id: newTableId, table_name: '', left: 50, top: 50, type };
  saveTable(newTable);

  if (removedIds.length > 0) {
      setRemovedIds((prev) => prev.slice(1));
  } else {
      setNextId((prev) => prev + 1);
  }
};

  

  const addNonTableItem = (type) => {
    const newNonTable = { left: 50, top: 50, type };
    saveNonTable(newNonTable);
  };

  const moveItem = (id, left, top, isTable, rotation) => {
    const endpoint = isTable ? "tables" : "nontables";
    const items = isTable ? tables : nonTableItems;

    const item = items.find((item) => item.id === id);

    if (!item) return;

    // Update state
    if (isTable) {
        setTables((prevTables) =>
            prevTables.map((i) => (i.id === id ? { ...i, left, top } : i))
        );
    } else {
        setNonTableItems((prevNonTables) =>
            prevNonTables.map((i) => (i.id === id ? { ...i, left, top } : i))
        );
    }

    // Update on backend
    const token = localStorage.getItem('token'); // Get the token from storage
    axios
        .put(
            `${SERVER_URL}${endpoint}/${id}/`,
            { ...item, left, top },
            {
                headers: { Authorization: `Token ${token}` }, // Include the token
            }
        )
        .catch((error) => console.error("Error updating item position", error));
};  

  const handleNameInputChange = async (e, tableId) => {
    const newName = e.target.value;
  
    // Update the table state locally
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === tableId ? { ...table, table_name: newName } : table
      )
    );
  
    // Send the update to the backend
    try {
      const tableToUpdate = tables.find((table) => table.id === tableId);

      if (tableToUpdate) {
        await axios.put(
          `${SERVER_URL}tables/${tableId}/`,
          { ...tableToUpdate, table_name: newName },
          { headers: { Authorization: `Token ${localStorage.getItem('token')}` } }
        );
      }
    } catch (error) {
      console.error("Error updating table name:", error);
    }
  };

  const openModal = async (table) => {
    setSelectedTable({ ...table, diners: [] }); // Initialize diners array
    setIsModalOpen(true);
  
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${SERVER_URL}get_diners_for_table`, {
        params: { table: table.id },
        headers: { Authorization: `Token ${token}` },
      });
  
      // Update selectedTable diners
      const updatedTable = { ...table, diners: response.data };
      setSelectedTable(updatedTable);
    } catch (error) {
      console.error("Error fetching diners for the table", error);
      alert("Failed to load diners for the table. Please try again.");
    }
  };
  
  

  const closeModal = () => {
    setSelectedTable(null);
    setIsModalOpen(false);
    setShowInviteList(false); // Reset invite list view
  };
  

  const handleItemClick = (id) => {
    setActiveItemId(id);
  };

  const handleDragStart = (e, id, isTable) => {
    e.preventDefault();
  
    // Determine the items (tables or nonTableItems)
    const items = isTable ? tables : nonTableItems;
    const item = items.find((item) => item.id === id);
  
    if (!item) return;
  
    // Store the initial mouse and item positions
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = item.left || 0;
    const startTop = item.top || 0;
  
    const handleMouseMove = (moveEvent) => {
      // Calculate new positions
      const newLeft = startLeft + (moveEvent.clientX - startX);
      const newTop = startTop + (moveEvent.clientY - startY);
  
      // Update state
      moveItem(id, newLeft, newTop, isTable); // Pass rotation
    };
  
    const handleMouseUp = () => {
      // Clean up event listeners
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  
    // Attach listeners to track dragging
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  
  const handleRotateItem = (id, isTable) => {
    const endpoint = isTable ? "tables" : "nontables";
    const items = isTable ? tables : nonTableItems;
  
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === id ? { ...table, rotation: (table.rotation || 0) + 90 } : table
      )
    );
  
    setNonTableItems((prevNonTables) =>
      prevNonTables.map((item) =>
        item.id === id ? { ...item, rotation: (item.rotation || 0) + 90 } : item
      )
    );
  
    const item = items.find((item) => item.id === id);
    if (!item) return;
  
    axios.put(
      `${SERVER_URL}${endpoint}/${id}/`,
      { ...item, rotation: (item.rotation || 0) + 90 },
      { headers: { Authorization: `Token ${localStorage.getItem('token')}` } }
    )
    .catch((error) => console.error("Error updating item rotation", error));
  };  
  
  

  const getSeatCount = (type) => {
    if (type.includes("10")) return 10;
    if (type.includes("11")) return 11;
    if (type.includes("12")) return 12;
    if (type.includes("13")) return 13;
    if (type.includes("14")) return 14;
    if (type.includes("18")) return 18;
    if (type.includes("24")) return 24;
    if (type.includes("32")) return 32;
    if (type.includes("48")) return 48;
    if (type.includes("20")) return 20;
    return 0;
  };


  const getInvitesWithoutTable  = async () => {
    try {
        const token = localStorage.getItem('token'); // Get the stored token
        const response = await axios.get(`${SERVER_URL}get_invites_no_table`, {
            headers: {
                Authorization: `Token ${token}`, // Include the token in the header
            },
        });
        setInvites(response.data);
    } catch (error) {
        console.error('Error getting invites', error);
    }
};

const filteredInvites = invites.filter((invite) =>
  invite.name.toLowerCase().includes(searchQuery.toLowerCase())
);

const handleSearchChange = (e) => {
  setSearchQuery(e.target.value);
};



return (
  <div>
    {showMobileWarning && (
  <div className="mobile-warning-overlay">
    <div className="mobile-warning-modal">
      <p>עמוד זה מותאם למחשב ואנו לא ממליצים להשתמש בו דרך הפלאפון</p>
      <div className="mobile-warning-buttons">
        <button onClick={() => setShowMobileWarning(false)}>המשך בכל זאת</button>
        <button onClick={() => window.history.back()}>בחזרה לעמוד הקודם</button>
      </div>
    </div>
  </div>
)}

    <div className="ButtonGrid">
      {/* Table buttons */}
      <div className="TableButtons">
        <button onClick={() => addTable(TABLE_TYPES.RECTANGLE18)}>אבירים 18 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.RECTANGLE24)}>אבירים 24 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.RECTANGLE32)}>אבירים 32 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.RECTANGLE48)}>אבירים 48 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.ROUND20)}>עגול 20 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE20)}>אבירים 20 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.ROUND10)}>עגול 10 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.ROUND11)}>עגול 11 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.ROUND12)}>עגול 12 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE10)}>מרובע 10 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE11)}>מרובע 11 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE12)}>מרובע 12 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE13)}>מרובע 13 סועדים</button>
        <button onClick={() => addTable(TABLE_TYPES.SQUARE14)}>מרובע 14 סועדים</button>
      </div>

      {/* Non-table item buttons */}
      <div className="NonTableButtons">
        <button onClick={() => addNonTableItem(TABLE_TYPES.DANCEFLOOR)}>רחבת ריקודים</button>
        <button onClick={() => addNonTableItem(TABLE_TYPES.DJSTATION)}>דוכן DJ</button>
        <button onClick={() => addNonTableItem(TABLE_TYPES.BARSTATION)}>תחנת בר</button>
        <button onClick={() => addNonTableItem(TABLE_TYPES.ENTRANCE)}>כניסה</button>
      </div>

      {/* Special buttons */}
      <div className="SpecialButtons">
        <button onClick={deleteAllItems}>מחק את כל הפריטים</button>
        <button onClick={() => downloadData("export-diners", "diners.xlsx")}>
          הורד סידור שולחנות
        </button>
        <button onClick={downloadImage}>הורד צילום שולחנות </button> 
      </div>
    </div>
    

    <div className="SummaryBox">
  <div>
    <span>מקומות ישיבה:</span>{" "}
    {tables.reduce((acc, table) => acc + getSeatCount(table.type), 0)}
  </div>
  <div>
    <span>מקומות תפוסים:</span> {dinersNumber || 0}
  </div>
  <div>
    <span>מקומות זמינים:</span>{" "}
    {tables.reduce((acc, table) => acc + getSeatCount(table.type), 0) - (dinersNumber || 0)}
  </div>
  <div>
    <span>שולחנות שסודרו:</span> {tables.length}
  </div>
</div>


    <div className="Canvas">
      {tables.map((item) => {
        const tableClass = `Table ${TYPE_TO_CLASS[item.type] || "DefaultTable"}`;
        console.log(item.type);

        return (
          <div
            className={tableClass}
            key={item.id}
            style={{
              left: `${item.left}px`,
              top: `${item.top}px`,
              transform: `rotate(${item.rotation || 0}deg)`, // Apply rotation
            }}
            onClick={() => handleItemClick(item.id)}
            onMouseDown={(e) => handleDragStart(e, item.id, true)}
          >
            <div className="table-id">{item.table_id}</div>
            <div className="diners-count">
              <div>
                {item.diners}/{getSeatCount(item.type)}
              </div>
            </div>
            <input
              className="table-name-input"
              type="text"
              value={item.table_name || ""}
              placeholder="שם השולחן"
              onChange={(e) => handleNameInputChange(e, item.id)}
              onMouseDown={(e) => e.stopPropagation()}
            />
            {activeItemId === item.id && (
                            <>
<button className="view-list-button" onClick={() => openModal(item)}>
      צפה ברשימה
    </button>

    <button
      className="change-id-button"
      onClick={() => {
        setSelectedTable(item); // update selected table
        setShowChangeIdPopup(item.table_id); // use table_id instead of boolean
        setNewTableIdInput(item.table_id.toString()); // pre-fill with current
      }}
    >
      שנה מספר שולחן
    </button>
    {showChangeIdPopup && (
  <>
    <div className="ChangeIdOverlay" onClick={() => setShowChangeIdPopup(false)} />
    <div
  className="ChangeIdPopup"
  onClick={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}  
>
      <h3>שנה מספר שולחן</h3>
      <input
        type="number"
        placeholder="מספר חדש"
        value={newTableIdInput}
        onChange={(e) => setNewTableIdInput(e.target.value)}
      />
      <div>
        <button
          onClick={() => {
            changeTableId(selectedTable.table_id, newTableIdInput);
            setShowChangeIdPopup(false);
            setNewTableIdInput('');
          }}
        >
          אישור
        </button>
        <button onClick={() => setShowChangeIdPopup(false)}>ביטול</button>
      </div>
    </div>
  </>
)}
              <div className="button-container">
  <div className="DeleteButton" onClick={() => deleteTable(item.id, item.table_id)}>
    X
  </div>
  <button
    className="rotate-button"
    onClick={(e) => {
      e.stopPropagation();
      handleRotateItem(item.id, true);
    }}
  >
    &#x21bb;
  </button>
</div>
</>
            )}
          </div>
        );
      })}
      {nonTableItems.map((item) => {
        const nonTableClass = `NonTableItem ${TYPE_TO_CLASS[item.type] || "DefaultNonTableItem"}`;

        return (
          <div
            className={nonTableClass}
            key={item.id}
            style={{
              left: `${item.left}px`,
              top: `${item.top}px`,
              transform: `rotate(${item.rotation || 0}deg)`, // Apply rotation
            }}
            onClick={() => handleItemClick(item.id)}
            onMouseDown={(e) => handleDragStart(e, item.id, false)}
          >
            {item.type}
            {activeItemId === item.id && (
              <div className="button-container">
  <div className="DeleteButton" onClick={() => deleteTable(item.id, item.table_id)}>
    X
  </div>
  <button
    className="rotate-button"
    onClick={(e) => {
      e.stopPropagation();
      handleRotateItem(item.id, false);
    }}
  >
    &#x21bb;
  </button>
</div>
)}

          </div>
        );
      })}
{isModalOpen && selectedTable && (
  <>
    <div className="Overlay" onClick={closeModal} />
    <div className="Modal">
      <h2>{`רשימת סועדים עבור שולחן ${selectedTable.table_name}`}</h2>

      {/* Show the default invite list */}
      <div className="diner-list">
        {selectedTable.diners.map((diner, index) => (
          <div className="diner-item" key={index}>
            <span>
              {diner.name} {diner.arriving > 1 ? `(${diner.arriving})` : ""}
            </span>
            <button
  className="diner-remove-button"
  onClick={() => {
    // Get the diner being removed
    const removedDiner = selectedTable.diners[index];

    // Update the diners list
    const updatedDiners = selectedTable.diners.filter((_, i) => i !== index);
    setSelectedTable({ ...selectedTable, diners: updatedDiners });

    // Save updated diners to the backend
    saveDiners(selectedTable.table_id, updatedDiners);

    // Add the removed diner back to the invites list
    setInvites((prevInvites) => [...prevInvites, removedDiner]);
  }}
>
  X
</button>
          </div>
        ))}
      </div>

      {/* Conditional rendering based on `showInviteList` */}
      {showInviteList ? (
        <>
          {/* Search Section */}
          <div className="invite-search-section">
            <input
              type="text"
              className="invite-search-input"
              placeholder="חפש אורח להוספה"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <div className="diner-list">
              {filteredInvites.map((invite) => (
                <div className="diner-item" key={invite.id}>
                  <span>
                    {invite.name} {invite.arriving > 1 ? `(${invite.arriving})` : ""}
                  </span>
                  <button
                    className="diner-add-button"
                    onClick={() => {
                      const totalSeatsTaken = selectedTable.diners.reduce(
                        (acc, diner) => acc + (diner.arriving || 1),
                        0
                      );
                      const newSeatsNeeded = invite.arriving || 1;
                      const totalSeats = getSeatCount(selectedTable.type);

                      if (totalSeatsTaken + newSeatsNeeded > totalSeats) {
                        alert("לא ניתן להוסיף את האורח - אין מספיק מקומות.");
                        return;
                      }

                      const updatedDiners = [
                        ...selectedTable.diners,
                        invite,
                      ];
                      setSelectedTable({
                        ...selectedTable,
                        diners: updatedDiners,
                      });
                      saveDiners(selectedTable.table_id, updatedDiners);

                      // Remove invite from the list
                      setInvites((prev) =>
                        prev.filter((existingInvite) => existingInvite.id !== invite.id)
                      );
                    }}
                  >
                    +
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
        <button
          className="add-invite-button"
          onClick={() => {
            getInvitesWithoutTable();
            setShowInviteList(true);
          }}
        >
          הוסף אורחים לשולחן זה
        </button>
    {showChangeIdPopup && (
      <div className="change-id-popup">
        <input
          type="number"
          placeholder="מספר חדש"
          value={newTableIdInput}
          onChange={(e) => setNewTableIdInput(e.target.value)}
        />
        <button
          onClick={() => {
            changeTableId(selectedTable.table_id, newTableIdInput);
            setShowChangeIdPopup(false);
            setNewTableIdInput('');
          }}
        >
          אישור
        </button>
        <button onClick={() => setShowChangeIdPopup(false)}>ביטול</button>
      </div>
    )}
  </>
)}
              {/* Close Button */}
      <button className="close-button" onClick={closeModal}>
        שמור וסגור
      </button>
    </div>
  </>
)}
    </div>
  </div>
);
}

export default Tables;  